import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaDocumentExportFormat } from "@/features/persona-opportunities/lib/persona-script-docx";

/** 44px on touch; the compact control height stays for a fine pointer. */
const EXPORT_TRIGGER_TOUCH_CLASS =
  "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8";
const EXPORT_ITEM_TOUCH_CLASS =
  "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-0";

export interface DocumentExportMenuProps {
  onExport: (format: PersonaDocumentExportFormat) => void;
  busy?: boolean;
  testId?: string;
  size?: "sm" | "default";
  className?: string;
  /** Drawer header keeps the download icon and puts "Export" in a tooltip. */
  iconOnly?: boolean;
  variant?: "outline" | "secondary";
}

/** Export opens a format menu. The download starts only after Text or Word is chosen. */
export function DocumentExportMenu({
  onExport,
  busy = false,
  testId,
  size = "sm",
  className,
  iconOnly = false,
  variant = "outline",
}: DocumentExportMenuProps) {
  const { t } = useTranslation();
  const exportLabel = t("personaOpportunities.scripts.bulk.export", "Export");
  const trigger = (
    <Button
      type="button"
      variant={variant}
      size={size}
      iconStart={busy ? undefined : "download"}
      disabled={busy}
      aria-busy={busy || undefined}
      aria-label={iconOnly ? exportLabel : undefined}
      data-testid={testId}
      className={cn(!iconOnly && EXPORT_TRIGGER_TOUCH_CLASS, className)}
    >
      {busy ? (
        <Icon
          name="loader"
          size="xs"
          color="inherit"
          className="shrink-0 animate-spin motion-reduce:animate-none"
          aria-hidden
        />
      ) : null}
      {iconOnly ? null : exportLabel}
    </Button>
  );

  return (
    <DropdownMenu>
      {iconOnly ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{exportLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className={EXPORT_ITEM_TOUCH_CLASS}
          onSelect={() => onExport("txt")}
          data-testid={testId ? `${testId}-txt` : undefined}
        >
          {t("personaOpportunities.scripts.bulk.exportText", "Text (.txt)")}
        </DropdownMenuItem>
        <DropdownMenuItem
          className={EXPORT_ITEM_TOUCH_CLASS}
          onSelect={() => onExport("docx")}
          data-testid={testId ? `${testId}-docx` : undefined}
        >
          {t("personaOpportunities.scripts.bulk.exportWord", "Word (.docx)")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
